import React from 'react';
import _GeneralDialog from '../../internal-components/GeneralDialog';
import Button from '../Button';
import { AnimationContainer } from './EmailDialogView';
import Animation from '../Animation';
import { Kjua } from 'react-kjua';
import InputBase from '../form/InputBase';
import { IconButton, InputAdornment } from '@material-ui/core';
import _LinkIcon from '@material-ui/icons/Link';
import styled from 'styled-components/macro';
import Colors from '../../internal-components/Colors';
import { NumberInput } from '../../internal-components/form-builder/view/RemindersStyledInputs';

const GeneralDialog = styled(_GeneralDialog)`
  && {
    .MuiDialogActions-root {
      display: none;
    }
  }
`;

const LinkIcon = styled(_LinkIcon)`
  && {
    color: ${Colors.PRIMARY};
  }
`;

const InstructionDiv = styled.div`
  font-size: 16px;
  line-height: 28px;
  margin-bottom: 1.5rem;
`;

const BoldWords = styled.span`
  font-weight: 500;
`;

const StepHeader = styled.div`
  font-weight: 600;
  margin-bottom: 0.75rem;
`;

const StyleInputBase = styled(InputBase)`
  [class*='MuiInputBase-root'] {
    background-color: #f7f8fc;
    margin: 1rem 0;
    width: 50%;
  }
  input {
    font-size: 16px !important;
  }
`;

const FullWidthButton = styled(Button)`
  && {
    margin-left: 0;
    width: 100%;
  }
`;

const StyledNumberInput = styled(NumberInput)`
  && {
    margin-bottom: 1rem;
    width: 106px;
  }
  input {
    letter-spacing: 3px;
  }
`;

const OneTimeCodesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background-color: #f7f8fc;
  padding: 1rem;
  border-radius: 0.5rem;
  align-items: center;
  letter-spacing: 3px;
`;

const OneTimeCodesRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
`;

const OneTimeCode = styled.div`
  padding: 1rem;
  border-radius: 0.5rem;
`;

const CodeButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
  justify-content: end;
  margin: 1rem;
`;

const MultiFactorAuthSetupDialogView = props => {
  return (
    <GeneralDialog
      open={props.open}
      title="Multi-factor authentication setup"
      handleClose={props.onClose}
      handleConfirm={props.onConfirm}
      buttons={[]}
    >
      <AnimationContainer>
        <Animation
          in={props.activeTab === 'one'}
          mountOnEnter // very important
          unmountOnExit // very important
          type="slide"
          direction="right"
          timeout={{
            enter: 250,
            exit: 100
          }}
        >
          <InstructionDiv>
            Multi-factor authentication has been enabled for you as an added
            security measure. Once this is set up you will be required to enter
            a code from an authentication app when logging in to your account.
          </InstructionDiv>

          <InstructionDiv>
            If you do not have access to an authentication app please contact an
            administrator.
          </InstructionDiv>

          <InstructionDiv>
            Click <BoldWords>Next</BoldWords> to view the steps required to set
            up multi-factor authentication.
          </InstructionDiv>

          <FullWidthButton onClick={props.onNext}>Next</FullWidthButton>
        </Animation>
        <Animation
          in={props.activeTab === 'two'}
          mountOnEnter // very important
          unmountOnExit // very important
          type="slide"
          direction="right"
          timeout={{
            enter: 250,
            exit: 100
          }}
        >
          <StepHeader>Step 1 - Open app</StepHeader>
          <InstructionDiv>
            If you don't already have an authenticator app, download one to your
            mobile device. An example of an authenticator app is{' '}
            <a
              href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2"
              target="_blank"
            >
              Google Authenticator
            </a>{' '}
            or{' '}
            <a
              href="https://www.microsoft.com/en-us/security/mobile-authenticator-app"
              target="_blank"
            >
              Microsoft Authenticator
            </a>
            .
          </InstructionDiv>
          <StepHeader>Step 2 - Scan code</StepHeader>
          <InstructionDiv>
            Add a new account in the app and scan the{' '}
            <BoldWords>QR code</BoldWords>.
          </InstructionDiv>
          <Kjua text={props.qrCode} size={150} />
          <div>Alternatively, you can enter the text code below.</div>
          <StyleInputBase
            readOnly
            value={props.userKey}
            variant="text"
            data-testid="authenticator-key"
            inputRef={props.linkRef}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={props.onLinkCopy}>
                    <LinkIcon />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          <StepHeader>Step 3 - Enter code</StepHeader>
          <InstructionDiv>
            Enter the <BoldWords>6-digit code</BoldWords> displayed and click{' '}
            <BoldWords>Login</BoldWords>.
          </InstructionDiv>
          <StyledNumberInput
            value={props.sixDigitCode}
            variant="outlined"
            onChange={props.onChangeSixDigitCode}
            onKeyDown={props.onSixDigitCodeKeyDown}
            data-testid="authenticator-code"
            placeholder="123 456"
          />
          <FullWidthButton onClick={props.onMFALogin}>Login</FullWidthButton>
        </Animation>
        {/* <Animation
          in={props.activeTab === 'one-time-codes'}
          mountOnEnter // very important
          unmountOnExit // very important
          type="slide"
          direction="right"
          timeout={{
            enter: 250,
            exit: 100
          }}
        >
          <InstructionDiv>
            Multi-factor authentication has successfully been set up on your
            account.
          </InstructionDiv>
          <InstructionDiv>
            Below are a list of single-use recovery codes, these codes can be
            used in the case that you are unable to use your authentication app.
            Please save these codes somewhere safe where you can access them if
            required.
          </InstructionDiv>
          <OneTimeCodesContainer>
            <OneTimeCodesRow>
              <OneTimeCode>{props.oneTimeCodes[0]}</OneTimeCode>
              <OneTimeCode>{props.oneTimeCodes[1]}</OneTimeCode>
              <OneTimeCode>{props.oneTimeCodes[2]}</OneTimeCode>
              <OneTimeCode>{props.oneTimeCodes[3]}</OneTimeCode>
            </OneTimeCodesRow>
            <OneTimeCodesRow>
              <OneTimeCode>{props.oneTimeCodes[4]}</OneTimeCode>
              <OneTimeCode>{props.oneTimeCodes[5]}</OneTimeCode>
              <OneTimeCode>{props.oneTimeCodes[6]}</OneTimeCode>
              <OneTimeCode>{props.oneTimeCodes[7]}</OneTimeCode>
            </OneTimeCodesRow>
          </OneTimeCodesContainer>
          <CodeButtonsContainer>
            <Button variant="text" onClick={props.copyCodes}>
              Copy codes
            </Button>
            <Button variant="text" onClick={props.downloadCodes}>
              Download codes
            </Button>
          </CodeButtonsContainer>
          <textarea
            ref={props.codesRef}
            style={{ position: 'absolute', left: '-9999px' }}
            readOnly
          />
          <FullWidthButton onClick={props.onMFALogin}>Login</FullWidthButton>
        </Animation> */}
      </AnimationContainer>
    </GeneralDialog>
  );
};

export default MultiFactorAuthSetupDialogView;
